import React, { useContext, useEffect, useReducer, useState } from 'react';
import {
  InputField,
  SelectField,
  CheckboxField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import { states } from '../FONASUCustomerInformation/util';
import styles from './FONASUJointOwner.css';
import { FlowContext } from '../../core/flow-engine';
import {
  formatSsnForDisplay,
  handleInputFormat
} from '../../flows/shared/utils';
import {
  stateValidation,
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  cityValidation,
  zipCodeValidation,
  restrictedFirstNameValidation,
  restrictedMiddleNameValidation,
  restrictedLastNameValidation
} from '../../flows/shared/validations';
import {
  customerSummaryLine1,
  customerSummaryLine3
} from '../ReviewCombinedPersonalInformation/util';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import { withDefault } from '../../core/util';

const FONASURenderJointOwnerDOBSSNInputs = ({ makeName, getDefault }) => {
  const {
    formProps: { jointOwnerDob, jointOwnerSsn }
  } = useContext(FlowContext);

  const [dobValue, setDobValue] = useState('');
  const [ssnValue, setSSNValue] = useState('');

  return (
    <>
      <div className="qv-col">
        <InputField
          type="text"
          title="Date of Birth"
          onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          {...jointOwnerDob}
          name={makeName(jointOwnerDob.name)}
          value={withDefault(dobValue, getDefault(jointOwnerDob.name))}
        />
      </div>
      <div className="qv-col">
        <InputField
          title="Social Security Number"
          placeholder="000-00-0000"
          maxLength="11"
          onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
          {...jointOwnerSsn}
          name={makeName(jointOwnerSsn.name)}
          value={withDefault(
            ssnValue,
            formatSsnForDisplay(getDefault(jointOwnerSsn.name))
          )}
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export const RenderBox = ({ additionalCustomers }) => {
  const {
    formProps: {
      jointOwnerFirstName,
      jointOwnerMiddleName,
      jointOwnerLastName,
      jointOwnerDob,
      jointOwnerCitizenship,
      jointOwnerSsn,
      jointOwnerSameAddress,
      jointOwnerResidentialStreet1,
      jointOwnerResidentialStreet2,
      jointOwnerResidentialZip,
      jointOwnerResidentialCity,
      jointOwnerResidentialState
    }
  } = useContext(FlowContext);

  // this reducer manages the varying checked/unchecked
  // states of the "same address" checkboxes
  const reducer = (state, idx) => {
    const prevButton = state[idx];
    const nextButtons = [...state];
    nextButtons[idx] = !prevButton;
    return nextButtons;
  };

  const [sameAddressChecked, toggleSameAddressChecked] = useReducer(
    reducer,
    [true, true, true].map((item, idx) =>
      additionalCustomers[idx] ? additionalCustomers[idx].sameAddress : item
    )
  );

  const defaults = {
    people: (additionalCustomers || []).map(person => ({
      [jointOwnerFirstName.name]: person.name.firstName,
      [jointOwnerMiddleName.name]: person.name.middleName,
      [jointOwnerLastName.name]: person.name.lastName,
      [jointOwnerDob.name]: person.dob,
      [jointOwnerCitizenship.name]: person.citizenship,
      [jointOwnerSsn.name]: person.taxID,
      [jointOwnerSameAddress.name]: person.sameAddress || true,
      [jointOwnerResidentialStreet1.name]: person.residentialAddress.street1,
      [jointOwnerResidentialStreet2.name]: person.residentialAddress.street2,
      [jointOwnerResidentialZip.name]: person.residentialAddress.zip,
      [jointOwnerResidentialCity.name]: person.residentialAddress.city,
      [jointOwnerResidentialState.name]: person.residentialAddress.state
    }))
  };

  const { sections, addSection } = useRepeatedSection('people', defaults);

  return (
    <div className="sections">
      {sections.map(({ key, getDefault, makeName, removeSection }, idx) => {
        return (
          <div key={key} className="section">
            {idx > 0 && (
              <div className="remove-section-button">
                <MFBeaconButton
                  id="fonasuJointOwnerRemoveBtn"
                  data-name="fonasuJointOwnerRemoveBtn"
                  type="button"
                  noButtonMargin
                  onClick={removeSection}
                  variant="tertiary"
                  label="X"
                />
              </div>
            )}
            <div className="review-section">
              <div className="personal-info">
                <div className="qv-row">
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="First Name"
                      {...jointOwnerFirstName}
                      name={makeName(jointOwnerFirstName.name)}
                      defaultValue={getDefault(jointOwnerFirstName.name)}
                      validation={restrictedFirstNameValidation}
                    />
                  </div>
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="Middle Name"
                      {...jointOwnerMiddleName}
                      name={makeName(jointOwnerMiddleName.name)}
                      defaultValue={getDefault(jointOwnerMiddleName.name)}
                      validation={restrictedMiddleNameValidation}
                    />
                  </div>
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="Last Name"
                      {...jointOwnerLastName}
                      name={makeName(jointOwnerLastName.name)}
                      defaultValue={getDefault(jointOwnerLastName.name)}
                      validation={restrictedLastNameValidation}
                    />
                  </div>
                </div>
                <div className="qv-row">
                  <FONASURenderJointOwnerDOBSSNInputs
                    makeName={makeName}
                    getDefault={getDefault}
                  />
                  <div className="qv-col">
                    <SelectField
                      title="Citizenship"
                      {...jointOwnerCitizenship}
                      name={makeName(jointOwnerCitizenship.name)}
                      defaultValue={getDefault(jointOwnerCitizenship.name)}>
                      <option value="US_CITIZEN">U.S. Citizen</option>
                      <option value="RESIDENT_ALIEN">
                        U.S. Resident Alien
                      </option>
                    </SelectField>
                  </div>
                </div>
              </div>

              <CheckboxField
                title="Use same contact information for Joint Owner"
                {...jointOwnerSameAddress}
                name={makeName(jointOwnerSameAddress.name)}
                defaultChecked={sameAddressChecked[idx]}
                onClick={() => toggleSameAddressChecked(idx)}
              />

              {!sameAddressChecked[idx] && (
                <div className="address-wrapper">
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Address Line 1"
                        {...jointOwnerResidentialStreet1}
                        name={makeName(jointOwnerResidentialStreet1.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialStreet1.name
                        )}
                        validation={[
                          addressLine1Validation,
                          poBoxLine1Validation
                        ]}
                        maxLength="35"
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Address Line 2"
                        {...jointOwnerResidentialStreet2}
                        name={makeName(jointOwnerResidentialStreet2.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialStreet2.name
                        )}
                        validation={[
                          addressLine2Validation,
                          poBoxLine2Validation
                        ]}
                        maxLength="35"
                      />
                    </div>
                  </div>
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="City"
                        {...jointOwnerResidentialCity}
                        name={makeName(jointOwnerResidentialCity.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialCity.name
                        )}
                        validation={cityValidation}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Zip Code"
                        {...jointOwnerResidentialZip}
                        name={makeName(jointOwnerResidentialZip.name)}
                        defaultValue={getDefault(jointOwnerResidentialZip.name)}
                        validation={zipCodeValidation}
                      />
                    </div>
                    <div className="qv-col">
                      <SelectField
                        title="State"
                        {...jointOwnerResidentialState}
                        name={makeName(jointOwnerResidentialState.name)}
                        defaultValue={getDefault(
                          jointOwnerResidentialState.name
                        )}
                        validation={stateValidation}>
                        <option hidden value="">
                          State
                        </option>
                        {states.map(item => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {sections.length < 3 && (
        <MFBeaconButton
          id="fonasuAddJointOwnerBtn"
          data-name="fonasuAddJointOwnerBtn"
          type="button"
          onClick={addSection}
          variant="tertiary"
          label="+ Add Joint Owner"
        />
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

export const shouldValidate = (additionalCustomers, editJointOwner) =>
  additionalCustomers.length > 0 || editJointOwner;

const FONASUJointOwner = ({
  editJointOwner,
  setEditJointOwner,
  showSuccess
}) => {
  const {
    next,
    appState: {
      store: {
        additionalCustomers = [],
        loadedData: { jointOwnerEditMode }
      }
    }
  } = useContext(FlowContext);

  const isValid = shouldValidate(additionalCustomers, editJointOwner);

  useEffect(() => {
    setEditJointOwner(jointOwnerEditMode);
  }, [jointOwnerEditMode]);

  const handleClick = async () => {
    if (editJointOwner) {
      await next({ section: 'jointOwners' })();
    } else {
      await next('unlockJointOwnerSection')();
    }
  };

  return (
    <>
      <div className="section-header">
        <SectionHeading text="Joint Owner(s)" />
      </div>
      {editJointOwner ? (
        <RenderBox additionalCustomers={additionalCustomers} />
      ) : (
        <div className="review-customer-information">
          {additionalCustomers.length ? (
            (additionalCustomers || []).map(person => (
              <div key={person.taxID} className="customer-information-lines">
                <div>{customerSummaryLine1(person)}</div>
                <div>{customerSummaryLine3(person)}</div>
              </div>
            ))
          ) : (
            <div>You have not added any joint owners yet.</div>
          )}
        </div>
      )}
      <EditButton
        onClick={handleClick}
        currentlyEditing={editJointOwner}
        disabled={!isValid || showSuccess}
      />
      <style jsx>{styles}</style>
    </>
  );
};

export default FONASUJointOwner;
